/* screen-configurator.js
 *
 * The custom-product configurator, running inside the product page. One section/template can now
 * serve any of the shapes configurator-core.js understands (two-axis, single-axis, three-axis with
 * depth, a surround/frame components picker, a variant-diameter switch) — see that file's header
 * for the full list. This file is the PDP-only shell around it: the launcher/band UI, the
 * stock-size /cart/add.js path, and the customer-info checkout dialog. Everything shape-adaptive
 * (spec-building, validation rules, drawing a shape the PDP never had before, the local price
 * estimate for a pricing.py-derived config) is delegated to the shared core.
 *
 * Two things stay deliberately PDP-owned rather than delegated, because their wording/behavior
 * already differs from the agent desk's and must not change for the product serving customers
 * today (SIGPRO Premium/Premier):
 *   - drawScreen(): the grommeted two-axis drawing, WITH the golfer-scale figure. The agent's
 *     equivalent (configurator-core.js's draw(), used here for every OTHER shape) has no figure —
 *     that's a customer-facing embellishment the internal order desk never needed.
 *   - problems() / renderRows() / showStock() / localEstimate(): customer-facing copy and (for
 *     localEstimate) a config shape — rate/setup/rounding_step/addon_price — that predates the
 *     pricing.py-derived ladder/components/derived shape the core expects. Each defers to the core
 *     only for a shape the CURRENT screen config can't produce (depth, components, a ladder, ...),
 *     so today's live product is byte-for-byte unchanged.
 *
 * Two checkout paths, decided by the server:
 *   stock size  -> the theme's own /cart/add, using the real variant, so Shop Pay and cart
 *                  merging keep working for the sizes most people buy
 *   custom size -> our checkout service creates a draft order and emails the invoice, because
 *                  Shopify cannot price a non-variant line any other way
 *
 * The browser never decides the price. It shows an instant local estimate so the UI feels alive,
 * then replaces it with the server's figure — which is also the only one used at checkout.
 */
(function () {
  'use strict';

  var GOLFER_PATHS = ["M 221.0,49.0 L 237.0,50.0 250.0,57.0 257.0,67.0 258.0,70.0 258.0,85.0 252.0,100.0 252.0,104.0 257.0,123.0 253.0,124.0 244.0,121.0 239.0,116.0 237.0,111.0 235.0,113.0 234.0,118.0 231.0,121.0 222.0,120.0 214.0,122.0 210.0,126.0 204.0,125.0 197.0,119.0 195.0,120.0 196.0,137.0 197.0,138.0 197.0,153.0 191.0,172.0 186.0,197.0 186.0,230.0 187.0,231.0 187.0,240.0 188.0,241.0 189.0,286.0 191.0,295.0 191.0,306.0 194.0,314.0 192.0,334.0 209.0,362.0 273.0,454.0 274.0,462.0 216.0,377.0 196.0,352.0 188.0,339.0 187.0,338.0 178.0,338.0 179.0,333.0 171.0,326.0 169.0,322.0 166.0,322.0 164.0,320.0 164.0,315.0 159.0,307.0 158.0,295.0 163.0,291.0 165.0,287.0 166.0,277.0 165.0,276.0 164.0,265.0 160.0,253.0 158.0,239.0 156.0,238.0 149.0,248.0 142.0,255.0 134.0,260.0 134.0,270.0 137.0,276.0 141.0,292.0 143.0,295.0 143.0,302.0 145.0,312.0 147.0,315.0 148.0,330.0 150.0,337.0 150.0,349.0 145.0,353.0 142.0,365.0 135.0,379.0 117.0,447.0 123.0,456.0 123.0,458.0 127.0,462.0 143.0,470.0 154.0,472.0 158.0,474.0 160.0,477.0 160.0,482.0 156.0,486.0 149.0,488.0 124.0,487.0 117.0,485.0 116.0,488.0 123.0,495.0 125.0,495.0 138.0,502.0 157.0,507.0 160.0,510.0 161.0,515.0 157.0,521.0 150.0,525.0 145.0,526.0 115.0,526.0 114.0,525.0 83.0,524.0 70.0,521.0 66.0,518.0 66.0,514.0 69.0,507.0 69.0,501.0 73.0,496.0 73.0,490.0 77.0,486.0 77.0,477.0 81.0,472.0 81.0,468.0 85.0,458.0 85.0,435.0 86.0,434.0 87.0,402.0 91.0,388.0 95.0,379.0 95.0,374.0 94.0,373.0 80.0,373.0 79.0,371.0 79.0,364.0 74.0,352.0 73.0,337.0 71.0,329.0 69.0,326.0 69.0,318.0 66.0,308.0 66.0,304.0 61.0,289.0 61.0,284.0 56.0,272.0 51.0,264.0 46.0,250.0 45.0,231.0 47.0,221.0 51.0,213.0 58.0,205.0 61.0,199.0 61.0,192.0 63.0,189.0 84.0,167.0 106.0,137.0 112.0,126.0 124.0,114.0 154.0,96.0 166.0,90.0 172.0,89.0 183.0,83.0 186.0,83.0 190.0,79.0 202.0,60.0 207.0,58.0 214.0,51.0 220.0,50.0 Z", "M 276.0,459.0 L 300.0,494.0 310.0,506.0 314.0,506.0 327.0,501.0 331.0,501.0 331.0,507.0 323.0,513.0 315.0,513.0 310.0,511.0 302.0,502.0 276.0,465.0 276.0,460.0 Z"];

  // Traced silhouette extents, in the reference image's pixel space. Scale is derived from the
  // gap between them so the figure is exactly FIGURE_H_IN tall — a scale reference that lies
  // about its own height defeats the purpose of the drawing.
  var ART_TOP = 49, ART_GROUND = 526;

  // Drawing constants, in INCHES — one SVG user unit is one inch.
  // From a production cut sheet: grommets on 12" centres, 6" at corners, 2" finished edge.
  var VINYL_IN = 2, GROMMET_SPACING_IN = 12, GROMMET_CORNER_IN = 6;
  // Drawn larger than life (a real grommet is about 1" across) and in red, because their job here
  // is to be legible at a glance on a 14 ft screen scaled to a phone, not to be to scale.
  var GROMMET_R_IN = 1.6, GROMMET_HOLE_IN = 0.62, GROMMET_COLOR = '#e0342a';
  // Drawn height, not standing height: a 6 ft person measures about 5'6" bent over the ball.
  var FIGURE_H_IN = 66, FIGURE_W_IN = 40, FIGURE_GAP_IN = 20, PAD_IN = 26;

  function money(n) {
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  function ftIn(n) { return Math.floor(n / 12) + "' " + (n % 12) + '"'; }
  function ftInTight(n) { return Math.floor(n / 12) + "'" + (n % 12) + '"'; }

  // Corner, another 6" in, then even ~12" centres, mirrored at the far end. Spacing across the
  // middle is distributed evenly so the run closes on the last grommet instead of leaving a stub.
  function grommets(start, end) {
    var span = end - start;
    if (span <= GROMMET_CORNER_IN * 2) return [start, end];
    var a = start + GROMMET_CORNER_IN, b = end - GROMMET_CORNER_IN;
    var gaps = Math.max(1, Math.round((b - a) / GROMMET_SPACING_IN));
    var step = (b - a) / gaps, pts = [start, a];
    for (var i = 1; i < gaps; i++) pts.push(a + i * step);
    pts.push(b, end);
    return pts;
  }

  function golfer(x, groundY) {
    var s = FIGURE_H_IN / (ART_GROUND - ART_TOP);
    var paths = GOLFER_PATHS.map(function (d) { return '<path d="' + d + '"/>'; }).join('');
    return '<g transform="translate(' + x + ' ' + (groundY - ART_GROUND * s) + ') scale(' + s +
           ')" fill="#9aa3ae">' + paths + '</g>';
  }

  // Read a response without assuming it is JSON. Shopify answers an unconfigured App Proxy path
  // with an HTML 404, and gateways return HTML on failure; calling r.json() on those throws
  // "Unexpected end of JSON input", which is not something a customer should ever be shown.
  function readJson(r) {
    return r.text().then(function (text) {
      var data = null;
      try { data = text ? JSON.parse(text) : null; } catch (ex) { data = null; }
      if (data) return { ok: r.ok, status: r.status, data: data };
      var msg = r.status === 404
        ? 'Our ordering service is not reachable from this page yet.'
        : 'We could not reach our ordering service (error ' + r.status + ').';
      return { ok: false, data: { error: msg }, raw: text ? text.slice(0, 200) : '' };
    });
  }

  function Configurator(root) {
    // Two attributes, not one: `data-config` is PDP-level (api path, launcher/band/checkout copy,
    // placement) and comes from section.settings; `data-product-config` is shape/pricing data from
    // product.metafields.custom.configurator_config, generated from pricing.py. Merging them here
    // rather than in Liquid keeps the section from having to hand-build one combined JSON blob.
    var cfg = Object.assign(
      JSON.parse(root.getAttribute('data-config')),
      JSON.parse(root.getAttribute('data-product-config') || '{}'));
    var api = cfg.api.replace(/\/$/, '');
    var el = {};
    ['h', 'hr', 'w', 'wr', 'hFt', 'wFt', 'd', 'dr', 'dFt', 'err', 'stage', 'price', 'priceSub',
     'stock', 'cta', 'ctaNote', 'rows', 'modal', 'done', 'doneLink'].forEach(function (k) {
      el[k] = root.querySelector('[data-sc="' + k + '"]');
    });

    // Easify (the option-set app used elsewhere on the site) still has a pre-migration option set
    // attached to some already-migrated products. Its own script overrides the theme's native price
    // display with its own (stale, wrong) total — the exact mechanism varies by product: sometimes
    // a separate .tpo_total-additional-price badge is shown instead, sometimes the native element
    // is hidden in place, and sometimes (SIG Custom DIY Golf Simulator Enclosure) a whole SECOND
    // .price--main__product element is injected as a sibling. A MutationObserver that fights this
    // continuously was tried and made things worse — Easify appears to react to losing by injecting
    // yet another duplicate, which is how "one wrong price" turned into "two prices shown at once"
    // on every migrated product, not just the five originally affected. This instead corrects the
    // DOM a handful of times on a fixed schedule (covering Easify's own async timing without an
    // open-ended fight) and, each time, treats anything beyond the FIRST .price--main__product as a
    // duplicate to hide — regardless of what Easify happens to tag it with — rather than assuming
    // one specific extra class name.
    if (cfg.min_price != null) {
      var correctPriceText = money(cfg.min_price) + ' USD';
      var fixNativePrice = function () {
        var kept = false;
        document.querySelectorAll('.price--main__product').forEach(function (el) {
          if (!kept) {
            if (el.textContent.trim() !== correctPriceText) el.textContent = correctPriceText;
            if (el.getAttribute('content') !== String(cfg.min_price)) {
              el.setAttribute('content', String(cfg.min_price));
            }
            el.style.setProperty('display', 'inline', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            kept = true;
          } else {
            el.style.setProperty('display', 'none', 'important');
          }
        });
        var tpoEl = document.querySelector('.tpo_total-additional-price');
        if (tpoEl) tpoEl.style.setProperty('display', 'none', 'important');
      };
      [0, 500, 1500, 3000, 6000].forEach(function (delay) {
        setTimeout(fixNativePrice, delay);
      });
    }

    function apiFn(path, options) { return fetch(api + path, options); }
    var core = window.ConfiguratorCore(cfg, el, apiFn, readJson, 'data-sc');

    var serverQuote = null, timer = null, seq = 0, busy = false;
    // Nothing expensive runs until the configurator is actually opened.
    var armed = false;

    // ── validation ──────────────────────────────────────────────────────────
    // The rules mirror configurator-core.js's problems() exactly (same bounds, same shape flags)
    // — only the wording stays PDP's own, spelled out for a customer rather than an agent.
    function problems(s) {
      var bad = [];
      if (!core.SINGLE && (!isFinite(s.height_in) || s.height_in < cfg.min_height
                            || s.height_in > cfg.max_height))
        bad.push(core.LBL.height + ' must be between ' + cfg.min_height + ' and ' + cfg.max_height +
                 ' inches.');
      if (!isFinite(s.width_in) || s.width_in < cfg.min_width || s.width_in > cfg.max_width)
        bad.push(core.LBL.width + ' must be between ' + cfg.min_width + ' and ' + cfg.max_width +
                 ' inches.');
      // Curtains only: width is a panel count, not a free measurement — each panel is
      // cfg.width_step inches wide (52", today), so only a multiple of that from the minimum is
      // buildable. No other product sets this field, so this check is otherwise a no-op.
      else if (cfg.width_step && (s.width_in - cfg.min_width) % cfg.width_step !== 0)
        bad.push(core.LBL.width + ' must be ' + cfg.min_width + '" plus a multiple of ' +
                 cfg.width_step + '" — one panel is ' + cfg.width_step + '" wide.');
      if (core.HAS_DEPTH && (!isFinite(s.depth_in) || s.depth_in < cfg.min_depth
                              || s.depth_in > cfg.max_depth))
        bad.push('Depth must be between ' + cfg.min_depth + ' and ' + cfg.max_depth + ' inches.');
      if (core.hasFinish() && !s.border_finish) bad.push('Please choose a border finish.');
      if (core.COMPONENTS && !(s.components || []).length)
        bad.push('Please choose at least one option.');
      return bad;
    }

    // ── drawing ─────────────────────────────────────────────────────────────
    // The grommeted screen, WITH the golfer figure — unchanged from before this file gained other
    // shapes. Every other shape (single-axis, depth, cut goods with no grommets) draws through the
    // shared core instead, which is strictly new capability rather than a change to this one.
    function drawScreen(s) {
      var h = s.height_in, w = s.width_in;
      var pocket = /pocket|3 sides/i.test(s.border_finish);
      var stageH = Math.max(h, FIGURE_H_IN);
      var figX = PAD_IN, scrX = figX + FIGURE_W_IN + FIGURE_GAP_IN;
      var groundY = PAD_IN + stageH, scrY = groundY - h;
      var vbW = scrX + w + PAD_IN * 2, vbH = groundY + PAD_IN * 1.6;

      // The border is a stroke straddling the rect edge, so the centre line of the black band IS
      // the rect edge — grommets sit on it, not inset from it, or they read as floating on the
      // screen face instead of punched through the vinyl.
      var dots = [];
      grommets(scrX + VINYL_IN * 2, scrX + w - VINYL_IN * 2).forEach(function (x) {
        dots.push([x, scrY]);
        if (!pocket) dots.push([x, groundY]);
      });
      grommets(scrY + VINYL_IN * 2, groundY - VINYL_IN * 2).forEach(function (y) {
        dots.push([scrX, y]);
        dots.push([scrX + w, y]);
      });

      var off = 13, tick = 3.5;
      var svg =
        '<rect x="' + scrX + '" y="' + scrY + '" width="' + w + '" height="' + h + '" fill="#f2f3f5"/>' +
        '<rect x="' + scrX + '" y="' + scrY + '" width="' + w + '" height="' + h +
          '" fill="none" stroke="#16181b" stroke-width="' + VINYL_IN * 2 + '"/>' +
        (pocket
          ? '<rect x="' + scrX + '" y="' + (groundY - 2) + '" width="' + w +
            '" height="2" fill="#16181b" opacity=".55"/><text x="' + (scrX + w / 2) + '" y="' +
            (groundY - 5) + '" font-size="7" text-anchor="middle" fill="#7b8492">2&quot; bottom pocket</text>'
          : '') +
        // A ring with a punched hole, not a dot — it has to read as a grommet at thumbnail size.
        dots.map(function (p) {
          return '<circle cx="' + p[0] + '" cy="' + p[1] + '" r="' + GROMMET_R_IN + '" fill="' +
                 GROMMET_COLOR + '"/><circle cx="' + p[0] + '" cy="' + p[1] + '" r="' +
                 GROMMET_HOLE_IN + '" fill="#ffffff"/>';
        }).join('') +
        golfer(figX, groundY) +
        '<line x1="' + PAD_IN * 0.5 + '" y1="' + groundY + '" x2="' + (vbW - PAD_IN * 0.5) +
          '" y2="' + groundY + '" stroke="#d8dce2" stroke-width="0.7"/>' +
        // width dimension
        '<line x1="' + scrX + '" y1="' + (groundY + off) + '" x2="' + (scrX + w) + '" y2="' +
          (groundY + off) + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<line x1="' + scrX + '" y1="' + (groundY + off - tick) + '" x2="' + scrX + '" y2="' +
          (groundY + off + tick) + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<line x1="' + (scrX + w) + '" y1="' + (groundY + off - tick) + '" x2="' + (scrX + w) +
          '" y2="' + (groundY + off + tick) + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<text x="' + (scrX + w / 2) + '" y="' + (groundY + off + 10) +
          '" font-size="9.5" text-anchor="middle" fill="#7b8492">' + w + '&quot; &#183; ' +
          ftInTight(w) + '</text>' +
        // height dimension
        '<line x1="' + (scrX + w + off) + '" y1="' + scrY + '" x2="' + (scrX + w + off) + '" y2="' +
          groundY + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<line x1="' + (scrX + w + off - tick) + '" y1="' + scrY + '" x2="' + (scrX + w + off + tick) +
          '" y2="' + scrY + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<line x1="' + (scrX + w + off - tick) + '" y1="' + groundY + '" x2="' + (scrX + w + off + tick) +
          '" y2="' + groundY + '" stroke="#7b8492" stroke-width="0.7"/>' +
        '<text x="' + (scrX + w + off + 6) + '" y="' + (scrY + h / 2) + '" font-size="9.5" fill="#7b8492"' +
          ' transform="rotate(90 ' + (scrX + w + off + 6) + ' ' + (scrY + h / 2) + ')"' +
          ' text-anchor="middle">' + h + '&quot; &#183; ' + ftInTight(h) + '</text>';

      el.stage.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
      el.stage.innerHTML = svg;
      el.stage.setAttribute('aria-label',
        'Scale drawing: ' + h + ' inch by ' + w + ' inch impact screen, ' + s.border_finish);
    }

    // configurator-core.js's own bar drawing (used for a single-axis product like baffle or foam
    // pad) sizes its canvas to exactly fit the CURRENT width, so every size fills the frame the
    // same way — it reads as zooming between sizes rather than visibly getting longer. The screen
    // drawing avoids this because the golfer figure is a fixed real-world reference; a bar has no
    // such anchor. Fixing the canvas to the product's MAXIMUM width instead gives it one, so the
    // bar's on-screen length actually reflects the real difference between sizes. Kept separate
    // from core.draw() rather than changed there, for the same reason drawScreen() is: that would
    // also change the agent desk's drawing.
    function drawBarFixed(s) {
      var w = s.width_in, maxW = cfg.max_width;
      var barH = Math.max(maxW * 0.06, 6), padX = maxW * 0.09;
      var vbW = maxW + padX * 2, vbH = barH + 46;
      var x = padX, y = 14, off = 14, tick = 3.5;
      // The label text is centered on the whole (fixed) canvas rather than on the bar's own
      // (x + w/2) midpoint. The canvas width is fixed to the product's MAXIMUM size on purpose (see
      // the comment above), so a small item's bar sits well to the left, close to x=0 — centering
      // the text there let it run off the left edge of the SVG for anything much narrower than
      // maxW (the valance at its 24" minimum against a 72" maxW, for example). Centering on vbW
      // instead guarantees the text always has equal room on both sides, regardless of how small
      // the bar is; for an item near maxW the two anchor points are nearly identical anyway, so
      // this is a no-op for anything already close to its own maximum.
      var textX = vbW / 2;
      var svg =
        '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + barH +
          '" fill="var(--screen)" stroke="var(--dim)" stroke-width="0.8"/>' +
        '<line x1="' + x + '" y1="' + (y + barH + off) + '" x2="' + (x + w) + '" y2="' +
          (y + barH + off) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
        '<line x1="' + x + '" y1="' + (y + barH + off - tick) + '" x2="' + x + '" y2="' +
          (y + barH + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
        '<line x1="' + (x + w) + '" y1="' + (y + barH + off - tick) + '" x2="' + (x + w) +
          '" y2="' + (y + barH + off + tick) + '" stroke="var(--dim)" stroke-width="0.7"/>' +
        '<text x="' + textX + '" y="' + (y + barH + off + 11) + '" font-size="8.5"' +
          ' text-anchor="middle" fill="var(--dim)">' + core.LBL.width.toUpperCase() + '  ' + w +
          '&quot; &#183; ' + ftIn(w) + '</text>';
      el.stage.setAttribute('viewBox', '0 0 ' + vbW + ' ' + vbH);
      el.stage.innerHTML = svg;
    }

    function draw(s) {
      if (core.SINGLE) return drawBarFixed(s);
      if (core.HAS_DEPTH || !core.HAS_GROMMETS) return core.draw(s);
      return drawScreen(s);
    }

    // ── local price estimate ───────────────────────────────────────────────
    // The legacy formula only knows rate/setup/rounding_step/addon_price, which is all today's
    // screen config has ever had — CFG.addons (a dict keyed by addon, for the ladder/derived
    // shape) doesn't exist there, so it must not be the path a screen product takes. Anything with
    // a richer, pricing.py-derived config (a ladder, components, depth, a single axis) goes
    // through the shared core instead, which is the only one of the two that knows that shape.
    function localEstimate(s) {
      if (core.HAS_DEPTH || core.SINGLE || core.COMPONENTS || cfg.ladder || cfg.setup_per_width
          || cfg.rate_len) {
        return core.localEstimate(s);
      }
      var raw = s.height_in * s.width_in * cfg.rate + cfg.setup;
      var cents = Math.round(raw * 100);
      var k = Math.ceil((cents + 1) / (cfg.rounding_step * 100));
      var screen = (k * cfg.rounding_step * 100 - 1) / 100;
      return screen + (s.addons.length ? cfg.addon_price : 0);
    }

    // A component past the first is priced the same way as the base one — the same base/per_sqft/
    // per_depth_ft ladder localEstimate() already applies via core.localEstimate — but it is
    // rendered inline next to its own checkbox instead of only showing up in the running total,
    // since a customer deciding whether to add the pipe frame kit needs to see what it costs
    // before checking the box, not after.
    function componentPrice(key, s) {
      var c = (cfg.components || {})[key];
      if (!c) return null;
      var sqft = s.width_in * s.height_in / 144;
      var depthFt = (s.depth_in || 0) / 12;
      var raw = c.base + c.per_sqft * sqft + c.per_depth_ft * (depthFt - 5);
      return core.to99((cfg.uplift || 1) * raw);
    }

    function renderComponentPrices(s) {
      root.querySelectorAll('[data-sc-component-amt]').forEach(function (out) {
        var key = out.getAttribute('data-sc-component-amt');
        var price = componentPrice(key, s);
        out.textContent = price == null ? 'not available at this size' : money(price);
      });
    }

    function render() {
      core.applyVariant();
      var s = core.spec();
      // Same call, same place, as agent/desk.js: a derived add-on (blackout screen, an
      // enclosure's landing pad) can go from offered to unavailable as the dimensions change, and
      // if it was checked when that happens, renderDerivedAddons() unchecks it and returns true —
      // re-read the spec so problems()/localEstimate() below see the box unchecked too, not a
      // stale spec still carrying an add-on the UI just turned off.
      if (core.renderDerivedAddons(s)) s = core.spec();
      var bad = problems(s);
      el.err.textContent = bad.join(' ');
      el.cta.disabled = bad.length > 0;
      renderComponentPrices(s);

      // A single-axis product (baffle, foam pad) has no height field in the markup at all — el.h
      // is null, so accessing it unconditionally threw here on every page load. That exception
      // propagated out of the whole init (nothing downstream catches it), which is why sc-active
      // never got set — leaving the native buy box visible underneath the launcher — and why the
      // drawing never rendered: everything after the crash, including draw(), never ran.
      [['h', 'hFt'], ['w', 'wFt']].forEach(function (pair) {
        if (!el[pair[0]] || !el[pair[1]]) return;
        var v = parseInt(el[pair[0]].value, 10);
        el[pair[1]].textContent = isFinite(v) ? ftIn(v) : '—';
      });
      if (el.dFt) {
        var dv = el.d ? parseInt(el.d.value, 10) : NaN;
        el.dFt.textContent = isFinite(dv) ? ftIn(dv) : '—';
      }
      // Add-ons as well as the finish: a chosen extra has to stay lit, or the only feedback that
      // it was added is the price moving.
      root.querySelectorAll('[data-sc="finish"], [data-sc="addon"], [data-sc="component"],' +
                            ' [data-sc="variant"]').forEach(function (i) {
        var box = i.closest('.sc__choice');
        if (box) box.classList.toggle('sc__choice--on', i.checked);
      });

      if (bad.length) {
        el.price.textContent = '—';
        el.priceSub.textContent = '';
        el.stock.classList.add('sc__hidden');
        return;
      }
      el.price.textContent = money(localEstimate(s));

      // Until the customer opens the band, this is all free: no drawing, no request. Drawing ~120
      // SVG nodes and calling /quote for a collapsed panel costs every visitor main-thread time
      // and a round trip, whether or not they ever configure anything.
      if (!armed) return;
      draw(s);
      refreshQuote();
    }

    // The server owns the price. It also knows which sizes are in stock, which the browser can't.
    function refreshQuote() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var s = core.spec();
        if (problems(s).length) return;
        var mine = ++seq;
        fetch(api + '/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(s)
        }).then(readJson).then(function (res) {
          var d = res.ok ? res.data : null;
          if (!d || mine !== seq) return;      // a newer edit already superseded this one
          serverQuote = d;
          el.price.textContent = money(parseFloat(d.total));
          el.priceSub.textContent = (core.SINGLE
            ? ''
            : (s.height_in * s.width_in / 144).toFixed(1) + ' sq ft · ') +
            d.lead_time + ' · plus tax';
          showStock(d.stock);
          renderRows(d);
        }).catch(function () { /* keep the local estimate */ });
      }, 250);
    }

    function showStock(stock) {
      if (!stock) { el.stock.classList.add('sc__hidden'); el.stock.innerHTML = ''; return; }
      var saving = parseFloat(stock.saving || '0');
      var savingTxt = saving > 0 ? ' You save ' + money(saving) + ' versus a custom cut.' : '';
      var lead = stock.lead_time ? stock.lead_time.toLowerCase() : '';
      var vs = (stock.lead_time && stock.custom_lead_time && stock.lead_time !== stock.custom_lead_time)
        ? ' instead of ' + stock.custom_lead_time.toLowerCase().replace(/^ships in /, '') : '';
      var dims = stock.height_in + '&quot; × ' + stock.width_in + '&quot;'
        + (stock.depth_in ? ' × ' + stock.depth_in + '&quot;' : '');

      if (stock.kind === 'exact') {
        // Deliberately generic, not "you get SKU-X for $Y": naming a substitute item reads as a
        // recommendation to switch, which is a confusing thing to say to someone who is already
        // sitting on that exact size — see the note above stock_candidates() in pricing.py for why
        // exact and near can never both apply to one quote, but the OLD copy still read that way.
        el.stock.innerHTML = '<strong>This is a stock size.</strong> ' +
          'Most stock sizes ship faster and offer savings versus a fully custom order.';
      } else {
        el.stock.innerHTML = '<strong>A stock size is very close to yours.</strong>' +
          stock.label + ' (' + dims + ') is ' +
          money(parseFloat(stock.price)) + (lead ? ', ' + lead : '') + vs + '.' + savingTxt +
          ' <button type="button" data-sc="useStock">Use the stock size</button>';
        var btn = el.stock.querySelector('[data-sc="useStock"]');
        btn.addEventListener('click', function () {
          // Same guard as render() above: a single-axis product has no height field to set.
          if (el.h && el.hr) el.h.value = el.hr.value = stock.height_in;
          el.w.value = el.wr.value = stock.width_in;
          if (el.d && stock.depth_in) el.d.value = el.dr.value = stock.depth_in;
          render();
        });
      }
      el.stock.classList.remove('sc__hidden');
    }

    // The server's line title carries the size ("… — 108" H x 192" W"), which the Finished size row
    // states two lines above. Drop it, and never let a cut land on a separator: truncating mid
    // " - " left a stray "- …" wrapped onto its own line.
    function lineLabel(title) {
      var label = String(title || '').split(' — ')[0].trim();
      // 64 clears the longest real product name; the cap is a guard against a pathological title,
      // not the normal path. Wrapping onto a second line is fine — the price column is pinned.
      if (label.length > 64) {
        label = label.slice(0, 62).replace(/[\s\-–—,:;]+$/, '') + '…';
      }
      return label;
    }

    function renderRows(d) {
      var s = core.spec();
      var rows = [
        core.SINGLE
          ? ['Finished ' + core.LBL.width.toLowerCase(), s.width_in + '"']
          : ['Finished size', s.height_in + '" H × ' + s.width_in + '" W'
                              + (core.HAS_DEPTH ? ' × ' + s.depth_in + '" D' : '')]
      ];
      if (s.border_finish) rows.push(['Border finish', s.border_finish]);
      d.lines.forEach(function (l) {
        rows.push([lineLabel(l.title), money(parseFloat(l.unit_price)), true]);
      });
      rows.push(['Lead time', d.lead_time]);
      // The third element marks a money value. Amounts hold their width and never wrap; text
      // values wrap inside their own column instead of overrunning the label beside them.
      el.rows.innerHTML = rows.map(function (r) {
        return '<div class="sc__row"><span>' + r[0] + '</span><span' +
               (r[2] ? ' class="sc__amt"' : '') + '>' + r[1] + '</span></div>';
      }).join('') +
        '<div class="sc__row sc__row--total"><span>Total (before tax)</span>' +
        '<span class="sc__amt">' + money(parseFloat(d.total)) + '</span></div>';

      var isStock = d.stock && d.stock.kind === 'exact';
      el.cta.textContent = isStock ? 'Add to cart' : 'Continue to checkout';
      el.ctaNote.textContent = isStock
        ? 'In stock — checkout as normal, including Shop Pay.'
        : 'Made to order. Not returnable if the measurements provided are incorrect.';
    }

    // ── checkout ──────────────────────────────────────────────────────────
    el.cta.addEventListener('click', function () {
      if (busy) return;
      var s = core.spec();
      if (problems(s).length) return;
      var isStock = serverQuote && serverQuote.stock && serverQuote.stock.kind === 'exact';
      if (isStock) return addStockToCart();
      return startCustomCheckout(s);
    });

    // A stock match is an ordinary variant, so it goes through the ordinary cart. Nothing bespoke
    // is being made, so there is no spec to carry and no draft order to create.
    function addStockToCart() {
      busy = true; root.classList.add('sc--busy');
      var variantId = serverQuote.stock.variant_id;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        window.location.href = '/cart';
      }).catch(function () {
        busy = false; root.classList.remove('sc--busy');
        el.err.textContent = 'Could not add that to your cart. Please try again.';
      });
    }

    // Everything the shop needs to build and ship the piece. Collected before the draft order is
    // created, so nobody has to chase an address afterwards.
    var FIELDS = core.FIELDS;

    function field(name) { return root.querySelector('[data-sc="' + name + '"]'); }
    function collect() { return core.collect(field); }

    function startCustomCheckout(s) {
      var modal = el.modal;
      if (!modal || !modal.showModal) return submitCustom(s);   // no dialog support: post as-is

      var recap = field('recap');
      if (recap) {
        recap.innerHTML = 'You are ordering<b>' + s.height_in + '" H × ' + s.width_in + '" W</b>' +
          s.border_finish + (serverQuote ? ' — ' + money(parseFloat(serverQuote.total)) : '');
      }
      field('modalErr').textContent = '';
      // Reopening after a completed order (customer backed out instead of following the link)
      // should offer the form again, not the stale confirmation from last time. el.done also
      // carries .sc__modal-inner, so — same reason as showDone() below — the `hidden` property
      // alone does not actually hide it; an inline style is required.
      var form = modal.querySelector('.sc__modal-inner:not([data-sc="done"])');
      if (form) form.style.removeProperty('display');
      if (el.done) { el.done.hidden = true; el.done.style.setProperty('display', 'none', 'important'); }
      modal.showModal();
    }

    // Mark the empty fields in place. A single red line under a long form sits below the fold on
    // most screens, so the field that needs attention has to say so itself.
    function markMissing(keys) {
      FIELDS.forEach(function (k) {
        var input = field(k);
        if (!input) return;
        var wanted = keys.indexOf(k) > -1;
        if (wanted && !input.classList.contains('sc__missing')) {
          input.setAttribute('data-sc-placeholder', input.getAttribute('placeholder') || '');
          input.setAttribute('placeholder', 'Required');
          input.classList.add('sc__missing');
          input.setAttribute('aria-invalid', 'true');
        } else if (!wanted) {
          clearMissing(input);
        }
      });
    }

    function clearMissing(input) {
      if (!input || !input.classList.contains('sc__missing')) return;
      var original = input.getAttribute('data-sc-placeholder') || '';
      if (original) input.setAttribute('placeholder', original);
      else input.removeAttribute('placeholder');
      input.removeAttribute('data-sc-placeholder');
      input.removeAttribute('aria-invalid');
      input.classList.remove('sc__missing');
    }

    if (el.modal) {
      var cancelBtn = field('cancel');
      if (cancelBtn) cancelBtn.addEventListener('click', function () { el.modal.close(); });

      var closeBtn = field('close');
      if (closeBtn) closeBtn.addEventListener('click', function () { el.modal.close(); });

      // Clicking the backdrop closes too. A click outside the inner form lands on the dialog
      // element itself, which is how the two are told apart.
      el.modal.addEventListener('click', function (ev) {
        if (ev.target === el.modal) el.modal.close();
      });

      // Clear a field's "Required" marker the moment the customer starts filling it in — leaving
      // it up while they type reads as though the typing isn't counting.
      FIELDS.forEach(function (k) {
        var input = field(k);
        if (input) input.addEventListener('input', function () { clearMissing(input); });
      });

      // Whatever route was used out, leave the button ready for the next attempt.
      el.modal.addEventListener('close', function () {
        var sb = field('submit');
        if (sb && !busy) setSubmitBusy(sb, false);
      });

      var submitBtn = field('submit');
      if (submitBtn) submitBtn.addEventListener('click', function () {
        var errEl = field('modalErr');
        var got = collect();
        markMissing(got.missing);
        if (got.missing.length) {
          errEl.textContent = 'Please complete the fields marked required.';
          var first = field(got.missing[0]);
          if (first) {
            if (first.scrollIntoView) first.scrollIntoView({ block: 'center' });
            first.focus();
          }
          return;
        }
        if (!core.emailValid(got.customer.email)) {
          errEl.textContent = 'Please enter a valid email address.';
          var em = field('email');
          if (em.scrollIntoView) em.scrollIntoView({ block: 'center' });
          em.focus();
          return;
        }
        errEl.textContent = '';
        setSubmitBusy(submitBtn, true);
        submitCustom(core.spec(), got.customer);
      });
    }

    // The draft-order request (a real Shopify Admin API call, not local) is the one step in this
    // whole flow with no local estimate to lean on — a plain disabled/dimmed button reads as stuck
    // for however many seconds it takes. A spinner says "working," not "broken."
    function setSubmitBusy(sb, isBusy) {
      sb.disabled = isBusy;
      sb.textContent = isBusy ? 'Creating your order…' : 'Continue to checkout';
      sb.classList.toggle('sc__btn--loading', isBusy);
    }

    // Swaps the address form for a one-button confirmation, rather than the instant redirect this
    // used to do. A hard `location.href` away from the page the moment a draft order exists gives
    // the customer no chance to notice it worked, and (see submitCustom) no email backs it up.
    function showDone(invoiceUrl) {
      // .sc__modal-inner sets display:flex unconditionally, which — being an ordinary author
      // rule — beats the browser's own default [hidden]{display:none}, so the `hidden` property
      // alone does nothing here (that's what left the old form's Back/Continue bar showing
      // underneath the confirmation). A plain inline style still lost to this — the theme
      // apparently sets it (or something touching it) with !important, which beats a normal
      // inline style. Setting the inline style's OWN priority to "important" is the one thing
      // that beats an author !important short of another inline !important, so this is as
      // forceful a hide as a browser allows.
      var form = el.modal.querySelector('.sc__modal-inner:not([data-sc="done"])');
      if (form) form.style.setProperty('display', 'none', 'important');
      if (el.doneLink) el.doneLink.setAttribute('href', invoiceUrl);
      // Undoes startCustomCheckout()'s forced hide from a prior reopen, if any — otherwise a
      // second completed order in the same page view would stay invisible here too.
      if (el.done) { el.done.hidden = false; el.done.style.removeProperty('display'); }
    }

    function submitCustom(s, customer) {
      busy = true; root.classList.add('sc--busy');
      el.cta.textContent = 'Creating your order…';

      // A quote may not have landed yet — the customer can reach this within the 250ms debounce.
      // Get one before submitting, so displayed_total is always a number the server produced.
      if (!serverQuote) {
        clearTimeout(timer);
        var mine = ++seq;
        return fetch(api + '/quote', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(s)
        }).then(readJson).then(function (res) {
          if (!res.ok) throw new Error(res.data.error || 'We could not price that.');
          if (mine === seq) serverQuote = res.data;
          busy = false; root.classList.remove('sc--busy');
          return submitCustom(s, customer);
        }).catch(function (ex) {
          busy = false; root.classList.remove('sc--busy');
          el.cta.textContent = 'Continue to checkout';
          // The customer is looking at the open modal at this point, not the band behind it —
          // el.err lives in the (now covered) band, so a message written only there is a message
          // nobody sees, and the disabled "Creating your order…" submit button never recovers.
          // This previously showed as an unexplained hang.
          var msg = ex.message || 'Something went wrong. Please try again.';
          var mErr = field('modalErr');
          if (el.modal && el.modal.open && mErr) {
            mErr.textContent = msg;
            var sb = field('submit');
            if (sb) setSubmitBusy(sb, false);
          } else {
            el.err.textContent = msg;
          }
        });
      }

      var body = Object.assign({}, s, {
        email: (customer && customer.email) || '',
        customer: customer || {},
        approved: true,
        // Only ever the server's own figure. Sending the local estimate here — which this
        // used to do whenever a quote had not landed yet — guarantees the price guard rejects
        // the order, because the estimate is the browser's approximation and the guard compares
        // exactly. If there is no server quote, ask for one first (below) rather than guess.
        displayed_total: serverQuote.total,
        // The draft order is still created either way (so an abandoned checkout is still a saved
        // lead) — this only skips Shopify's own "Pay now" email. Without it, a customer who pays
        // on the confirmation screen below could still get that email afterward and wonder if
        // they need to act on it. The confirmation screen is the one and only prompt to pay.
        send_invoice: false
      });
      fetch(api + '/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(readJson).then(function (res) {
        if (!res.ok) {
          if (res.raw && window.console) console.error('[screen-configurator]', res.raw);
          // 409 is the price guard. The held quote is the stale thing, so drop it and re-price
          // — otherwise pressing the button again sends the same figure and is refused again.
          if (res.status === 409) {
            serverQuote = null;
            if (res.data.total) el.price.textContent = money(parseFloat(res.data.total));
            refreshQuote();
            throw new Error(res.data.error + (res.data.total
              ? ' It is now ' + money(parseFloat(res.data.total)) + '.' : ''));
          }
          throw new Error(res.data.error || 'We could not create your order.');
        }
        busy = false; root.classList.remove('sc--busy');
        el.cta.textContent = 'Continue to checkout';
        showDone(res.data.invoice_url);
      }).catch(function (ex) {
        busy = false; root.classList.remove('sc--busy');
        el.cta.textContent = 'Continue to checkout';
        var msg = ex.message || 'Something went wrong. Please try again.';
        var mErr = field('modalErr');
        if (el.modal && el.modal.open && mErr) {
          mErr.textContent = msg;                       // keep the form open so nothing is retyped
          var sb = field('submit');
          if (sb) setSubmitBusy(sb, false);
        } else {
          el.err.textContent = msg;
        }
      });
    }

    // ── wiring ────────────────────────────────────────────────────────────
    [['h', 'hr'], ['w', 'wr'], ['d', 'dr']].forEach(function (pair) {
      var num = el[pair[0]], rng = el[pair[1]];
      if (!num || !rng) return;
      num.addEventListener('input', function () { rng.value = num.value; render(); });
      rng.addEventListener('input', function () { num.value = rng.value; render(); });
    });
    root.querySelectorAll('[data-sc="finish"], [data-sc="addon"], [data-sc="addon-pick"],' +
                          ' [data-sc="component"], [data-sc="variant"]').forEach(function (i) {
      i.addEventListener('change', render);
    });

    // The band lives below the gallery, which is where the section already renders — so nothing
    // large needs relocating. Only the launcher is injected into the buy-box slot, and if that
    // anchor is ever missing we lose a button rather than the whole configurator.
    var band = document.getElementById(cfg.band_id);

    function openBand(scroll) {
      if (!band) return;
      band.hidden = false;
      armed = true;                   // from here on, drawing and quoting are worth their cost
      if (scroll) band.scrollIntoView({ behavior: 'smooth', block: 'start' });
      render();                       // first paint happens here, once the band has real width
    }

    // The launcher's "Starting at" price is now rendered server-side by Liquid from
    // pcfg.min_price (computed by the same pricing.py functions /quote uses — see
    // sync_client_config.py's _min_price) — correct even for a crawler that never runs this
    // script, so there is nothing left for this shell to compute or override here.
    var tpl = document.querySelector('[data-sc-launch]');
    var anchor = cfg.anchor_selector ? document.querySelector(cfg.anchor_selector) : null;
    if (tpl && anchor && anchor.parentNode) {
      var launcher = tpl.content.cloneNode(true);
      var openBtn = launcher.querySelector('[data-sc-open]');
      if (openBtn) openBtn.addEventListener('click', function () { openBand(true); });
      anchor.parentNode.insertBefore(launcher, anchor);
    } else if (band) {
      // No buy box to attach to, so there would be no way to open the band. Show it rather than
      // leaving the product with no way to configure anything.
      band.hidden = false;
    }

    if (cfg.open_on_load || (band && !band.hidden)) openBand(false);
    else render();                    // keep values in sync even while collapsed
  }

  function init() {
    document.querySelectorAll('[data-screen-configurator]').forEach(function (root) {
      if (root.dataset.scReady) return;
      root.dataset.scReady = '1';
      try {
        // `sc-active` is the switch that hides the theme's own buy box. Setting it first means a
        // throw below un-sets it, so a broken configurator can never leave the product unbuyable.
        document.documentElement.classList.add('sc-active');
        Configurator(root);
      } catch (ex) {
        document.documentElement.classList.remove('sc-active');
        if (window.console) console.error('[screen-configurator]', ex);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
